// Tiles widget — one tile per finding / defect on the active scope.
//
// R58-L-14 overhaul (2026-05-20):
//   * Header controls: Current/All scope toggle (OQ-10 default = current
//     image) + AI/Defect multi-select chips. Toggles persisted to URL
//     (`?tilesScope` + `?tilesKinds`) — see `useTilesScope`.
//   * Tiles grouped: AI findings block first, then a visible separator
//     (`data-testid="tiles-section-divider"`), then defects. Divider
//     only renders when BOTH kinds are checked AND both blocks have
//     content.
//   * Scope=`all` widens the source pool from "findings on
//     activeImageId" to "findings on any image". The `Tile` component
//     looks up its source image via the per-id map below so the crop
//     can find pixels even when the user is currently looking at a
//     different image.
//
// History: previous to this story, tiles only rendered AI findings on
// the active image. The 2026-05-09 contract bump made defects the
// primary citizen; the 2026-05-20 OQ-10 lock added the Current/All
// toggle independent of the List widget.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Fade, LinearProgress, Stack, Typography } from "@mui/material";
import { StatusPill } from "@aeriosiq/ui";
import type { AiFinding, Defect, ImageRef } from "@aeriosiq/types";
import { useImagesQuery } from "../../queries/useImagesQuery.js";
import { useImageActivationStore } from "../../state/imageActivationStore.js";
import {
  useDefectsQuery,
  statusToStatusPill,
} from "../../../defects/queries.js";
import { DefectGradeChip } from "../../../defects/DefectGradeChip.js";
import { Tile } from "../../components/Tile.js";
import { useSelectionStore } from "../../state/selectionStore.js";
import { useAnalysisFilterStore } from "../../state/analysisFilterStore.js";
// SB6-ANA-FILT-05 — the Tiles "All images" scope reduces to the shared
// filtered DATA-POINT set, like the Filmstrip + List.
import { useFilteredDataPoints } from "../../useFilteredDataPoints.js";
// ANA-R1-04 — shared raw-class-name → model display-name lookup so the AI tiles'
// accessible labels render the canonical display name, matching the List + AI
// filter module.
import { useClassDisplayNames } from "../../useClassDisplayNames.js";
// ANA-R1-02 — the single effective AI-finding set shared with the List and
// Viewer. The findings source narrows per finding by the committed AI-class
// confidence filter (a sub-threshold finding on an otherwise-passing image
// gets no tile) AND uses the identical (state-agnostic) narrowing the Viewer
// uses, so tile count == bbox count == list count for every image.
import { useEffectiveFindingSet } from "../../useEffectiveFindingSet.js";
// SB6-ANA-TILE-02 — Undo button moved to AnalysisHeader top bar. The
// import + the in-widget toolbar are gone; the action is now reachable
// from <UndoRedoCluster /> in AnalysisHeader.
import { TilesHeaderControls } from "../../tiles/TilesHeaderControls.js";
// ANA-R2-07 — the Promote-selected action moved out of the resting toolbar into
// a contextual action bar that appears only when ≥1 tile is selected.
import { TilesActionBar } from "../../tiles/TilesActionBar.js";
import { useTileSelection } from "../../tiles/useTileSelection.js";
import {
  useTilesScope,
  useTilesScopeUrlSync,
} from "../../tiles/useTilesScope.js";
// ANA-R2-06 — the scope toggle counts derive from the SAME shared sources the
// render path uses. The pure `scopeFindings`/`scopeDefects` helpers are the
// single intersection logic for both the rendered tiles and the header counts,
// so the badge can never diverge from the grid.
import {
  scopeFindings,
  scopeDefects,
  useTilesScopeCounts,
  ALL_SCOPE_FINDINGS_PAGE_SIZE,
  type TilesScopeInputs,
} from "../../tiles/useTilesScopeCounts.js";
// ANA-R3-TILE-03 — user-selectable tile size (3 presets). The chosen size
// drives the grid cell floor (minmax) + gridAutoRows + every tile's square
// box; the Tile canvas reads the same preset via useTileSettings.
import { useTileSettings } from "../../viewer/useTileSettings.js";
// ANA-R4-TILE-DEFECT-COLOR — per-kind bbox outline colours from user settings.
import { useLysePreferencesQuery } from "../../../preferences/queries.js";
import { TILE_SIZE_PX } from "../../components/tileCrop.js";
// ANA-R3-GEOM-05 — session-only geometry overrides; reset on DA switch (AC5).
import { useTileGeometryOverrideStore } from "../../tiles/tileGeometryOverrideStore.js";

interface TilesWidgetProps {
  /** Inspection / data acquisition id — same value, two names in the codebase. */
  dataAcquisitionId: string;
  /**
   * Story 064 — render the undo button in a Tiles-widget toolbar.
   * Defaults to true. Set to false when the surrounding capability
   * gate refuses edits (e.g. EVALUATION_DONE / LOCKED) — the button
   * will still render but locks into the "Nothing to undo" state.
   */
  editAllowed?: boolean;
}

// ANA-R3-TILE-01-FIX — render bound for the "All images" scope. Removing the
// implicit 200-row cap means the All-images scope can resolve to thousands of
// findings on a finding-heavy DA; each rendered tile mounts a <canvas> that
// downloads + crops a full-res image (Tile.tsx), so mounting them all at once
// is not viable (the story's volume guard, AC3). Until the grid is fully
// virtualised (ANA-R3-TILE-02 follow-up — windowed grid + render-progress
// bar), we cap the rendered tile count and surface a visible "showing first N"
// affordance. The "Current image" scope is NOT capped — a single image's
// findings are well under this bound. NOTE: this caps RENDERED tiles only; the
// header All-images badge still counts the FULL set (useTilesScopeCounts), so
// the badge and the "showing first N of M" line tell the user the true total.
const ALL_SCOPE_RENDER_CAP = 300;

export function TilesWidget({
  dataAcquisitionId,
  editAllowed = true,
}: TilesWidgetProps): React.ReactElement {
  // R58-L-14 — mount the URL-sync once per widget so the scope + kinds
  // round-trip through the address bar (story AC2 invariant).
  useTilesScopeUrlSync();

  // ANA-R3-GEOM-05 — bind the session override store to the active DA. When the
  // DA changes (a different inspection opens) every session geometry override is
  // dropped (AC5 — overrides never leak across DAs). Idempotent for the same id
  // so re-renders never wipe an in-progress edit. Reload clears them too (the
  // store is re-created on every page load).
  const resetOverridesForDa = useTileGeometryOverrideStore(
    (s) => s.resetForDataAcquisition,
  );
  useEffect(() => {
    resetOverridesForDa(dataAcquisitionId);
  }, [dataAcquisitionId, resetOverridesForDa]);

  // ANA-R3-TILE-03 — the user-selected tile-size preset in px. Drives the
  // grid cell floor + gridAutoRows here AND each tile's square box; Tile's
  // canvas reads the same preset directly via useTileSettings.
  const { tiles } = useTileSettings();
  const { prefs } = useLysePreferencesQuery();
  const aiOutlineColor = prefs.boundingBox.ai.color;
  const tileSidePx = TILE_SIZE_PX[tiles.tileSize];

  const imagesQuery = useImagesQuery(dataAcquisitionId);
  const activeImageId = useImageActivationStore((s) => s.activeImageId);
  const tilesScope = useTilesScope((s) => s.scope);
  const tilesKinds = useTilesScope((s) => s.kinds);
  // ANA-R3-TILE-01-FIX — the tiles' findings source is now scope-dependent,
  // mirroring useTilesScopeCounts so the grid and the header badge stay
  // byte-identical (ANA-R2-06):
  //   * "current" → IMAGE-SCOPED set (the same per-image query the List uses).
  //     This fixes the undercount: the previous dataset-wide source defaulted
  //     to a 200-row page with no page-walk, so any image whose findings fell
  //     past the first 200 DA rows showed fewer tiles than the List (5 → 2).
  //   * "all" → DATASET-WIDE set, page-walked to completeness via the large
  //     pageSize, so the All-images scope sees every finding (not the first
  //     200) — then render-capped below to honour the volume guard (AC3).
  // Narrowing (aiClassFilter, no state) is identical to the List + Viewer
  // (ANA-R1-02). Both sets are read unconditionally to keep hook order stable.
  const currentFindingSet = useEffectiveFindingSet(dataAcquisitionId, {
    imageId: activeImageId,
  });
  const allFindingSet = useEffectiveFindingSet(dataAcquisitionId, {
    pageSize: ALL_SCOPE_FINDINGS_PAGE_SIZE,
  });
  const findingsQuery =
    tilesScope === "current" ? currentFindingSet.query : allFindingSet.query;
  const defectsQuery = useDefectsQuery(dataAcquisitionId, { pageSize: 200 });

  // For scope=`all` we need every image (so each tile can find its
  // source pixels). For scope=`current` we only need the active image
  // — but keeping the index uniformly means the tile-render branch
  // doesn't need to special-case the scope.
  const imageById = useMemo(() => {
    const map = new Map<string, ImageRef>();
    for (const it of imagesQuery.data?.items ?? []) map.set(it.imageId, it);
    return map;
  }, [imagesQuery.data]);

  const activeImage: ImageRef | null = activeImageId
    ? (imageById.get(activeImageId) ?? null)
    : null;

  // Apply the analysis-filter store (Blade / Side / Severity / Status
  // chips from FilterToolbar) on top of the per-image filter, so the
  // tiles obey the same cascade as the rest of the analysis surface.
  const blades = useAnalysisFilterStore((s) => s.blades);
  const sides = useAnalysisFilterStore((s) => s.sides);
  const severities = useAnalysisFilterStore((s) => s.severities);
  const statuses = useAnalysisFilterStore((s) => s.statuses);

  // SB6-ANA-FILT-05 — the Global Data Point Filter result. For scope=all
  // the tiles only show annotations whose image is in the filtered set.
  const filtered = useFilteredDataPoints(dataAcquisitionId);
  const filteredImageIds = filtered.visibleImageIds;
  const filteredPending = filtered.isPending;

  // ANA-R1-04 — resolve raw class names to model display names for the AI tiles.
  const classNames = useClassDisplayNames(dataAcquisitionId);

  // ANA-R2-06 — both scope counts for the header toggle, from the same shared
  // sources as the render path below.
  const scopeCounts = useTilesScopeCounts(dataAcquisitionId);

  // The per-scope intersection used for THIS render — reuses the same pure
  // helpers the counts use (so the grid and the header badge agree exactly).
  const scopeInputs: TilesScopeInputs = useMemo(
    () => ({
      scope: tilesScope,
      activeImageId,
      filteredImageIds,
      filteredPending,
    }),
    [tilesScope, activeImageId, filteredImageIds, filteredPending],
  );

  const defectsInScope: Defect[] = useMemo(() => {
    if (!tilesKinds.has("defect")) return [];
    return scopeDefects(defectsQuery.data?.items ?? [], scopeInputs, {
      blades,
      sides,
      severities,
      statuses,
    });
  }, [
    defectsQuery.data,
    tilesKinds,
    scopeInputs,
    blades,
    sides,
    severities,
    statuses,
  ]);

  // Full in-scope finding set (uncapped) — used for the true total and the
  // "showing first N of M" affordance. The RENDERED slice is capped below.
  const findingsInScopeAll: AiFinding[] = useMemo(() => {
    if (!tilesKinds.has("ai")) return [];
    return scopeFindings(findingsQuery.data?.items ?? [], scopeInputs);
  }, [findingsQuery.data, tilesKinds, scopeInputs]);

  // ANA-R3-TILE-01-FIX — render bound for the "all" scope only. "current" is a
  // single image (well under the cap) and is never truncated, so it keeps exact
  // List parity (AC1). For "all" we render at most ALL_SCOPE_RENDER_CAP tiles
  // to avoid mounting thousands of cropping canvases at once (AC3); the header
  // badge still reflects the full count and the affordance below names the
  // total. Full virtualisation is the ANA-R3-TILE-02 follow-up.
  const findingsTruncated =
    tilesScope === "all" && findingsInScopeAll.length > ALL_SCOPE_RENDER_CAP;
  const findingsInScope: AiFinding[] = useMemo(
    () =>
      findingsTruncated
        ? findingsInScopeAll.slice(0, ALL_SCOPE_RENDER_CAP)
        : findingsInScopeAll,
    [findingsInScopeAll, findingsTruncated],
  );

  const findingById = useMemo(() => {
    const map = new Map<string, AiFinding>();
    for (const f of findingsQuery.data?.items ?? []) map.set(f.id, f);
    return map;
  }, [findingsQuery.data]);

  // ANA-R3-TILE-02 — render-progress aggregation. `total` is the set of tiles
  // the grid actually mounts this render (AI + defects, INCLUDING the
  // ANA-R2-01 placeholders — they're already part of these two arrays). Reusing
  // the same memoised in-scope arrays the grid maps over means `total` can
  // never disagree with the visible tile count (AC2). Called BEFORE the
  // empty-state early returns so its hooks run unconditionally (rules of hooks).
  const renderProgress = useTilesRenderProgress(
    findingsInScope,
    defectsInScope,
  );

  // Cross-widget tile selection — reading at the top so hook order is
  // stable across the empty-state early returns below.
  //
  // R58-L-10: the multi-select handler now flows through `useTileSelection`
  // (a thin facade over `selectionStore`) so the click adapter and the
  // Promote button share one source of truth. The store still exposes
  // `activeFindingId` directly for the per-Tile `active` prop.
  const activeFindingId = useSelectionStore((s) => s.activeFindingId);
  const { selectedIds: selectedFindingIds, handleClick: handleTileClick } =
    useTileSelection();

  // SB6-ANA-TILE-01 — tile clicks now open the source recording image in
  // the Viewer + Filmstrip, mirroring the List widget (ListWidget.tsx:
  // 240–249 / 397–402). `setActiveImage` is the canonical activation
  // action (Viewer + Filmstrip subscribe); `setActiveBbox` publishes the
  // selected defect with its kind so the List auto-switches to its
  // Defects tab (R58-L-13 reverse-direction effect).
  const setActiveImage = useImageActivationStore((s) => s.setActive);
  const setActiveBbox = useSelectionStore((s) => s.setActive);

  // SB6-ANA-TILE-02 — the in-widget Undo toolbar has been lifted into
  // <UndoRedoCluster /> in the AnalysisHeader top bar. Story 064's
  // per-image stack still drives the button (same store, same trigger)
  // — the affordance just lives in the top bar now.
  //
  // ANA-R2-07 — the resting toolbar is grouped FILTERS ONLY (scope + content
  // kind, with the R2-06 counts); the **Promote selected** action no longer
  // lives here. It surfaces in the contextual <TilesActionBar /> below only
  // when ≥1 tile is selected.
  const headerChrome = (
    <TilesHeaderControls
      currentCount={scopeCounts.current}
      allCount={scopeCounts.all}
    />
  );

  // ANA-R2-07 — the selection action bar (renders nothing while nothing is
  // selected). Mounted right under the header so it slides in above the grid.
  const actionBar = (
    <TilesActionBar
      inspectionId={dataAcquisitionId}
      editAllowed={editAllowed}
    />
  );

  if (tilesScope === "current" && !activeImage) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {headerChrome}
        <CenteredMessage>
          Select an image in the Filmstrip to see its defect tiles.
        </CenteredMessage>
      </Box>
    );
  }

  if (defectsInScope.length === 0 && findingsInScope.length === 0) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {headerChrome}
        <CenteredMessage>No findings on this image</CenteredMessage>
      </Box>
    );
  }

  // Cross-widget selection wiring — plain click selectOne, shift-click
  // selectRange against the visible finding order, ctrl/cmd-click
  // toggleSelection. SB6-ANA-TILE-01: defect tiles now activate their
  // source image + select the defect on plain click (single-select only;
  // no modifier multi-select — the ListWidget's Defects tab still owns
  // bulk defect interactions).
  const findingIdsInOrder = findingsInScope.map((f) => f.id);

  // Divider only renders when BOTH blocks have content. Otherwise the
  // single-kind path stays clean (no extra hairline above a single
  // block).
  const showDivider = findingsInScope.length > 0 && defectsInScope.length > 0;

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
      }}
    >
      {headerChrome}
      {actionBar}
      {/* SB6-ANA-TILE-01 — vertical-scroll square grid. Replaces the
          pre-SB6 horizontal Stack that produced the "stretched
          rectangles in a horizontal strip" Christian flagged at
          [01:29:32]. The grid auto-fits as many TILE_SIDE-wide columns
          as the widget can host, wraps to the next row when full, and
          scrolls vertically (never horizontally). Tiles keep their
          square aspect because each tile owns its own width/height
          inside Tile.tsx; the grid only governs placement. */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowX: "hidden",
          overflowY: "auto",
          p: 1,
        }}
      >
        <Box
          role="listbox"
          aria-label="Findings and defects in scope"
          sx={{
            display: "grid",
            gridTemplateColumns: `repeat(auto-fill, minmax(${String(tileSidePx)}px, 1fr))`,
            gridAutoRows: `${String(tileSidePx)}px`,
            gap: 1,
            alignContent: "start",
          }}
        >
          {/* AI findings block — rendered first per R58-L-14 AC3. */}
          {findingsInScope.map((f) => {
            const img = imageById.get(f.imageId);
            // ANA-R2-01 AC2 — NEVER drop a tile when its source image
            // metadata hasn't resolved yet (images query still in flight, or
            // the image is outside the current page). Dropping it produced
            // the exact bug this story reopens: the count says "Findings (2)"
            // while the grid renders empty (every tile `return null`-ed). A
            // placeholder keeps the on-screen tile count == the List count;
            // it upgrades to the real crop on the next render once the image
            // resolves.
            if (!img) {
              return (
                <PlaceholderTile
                  key={f.id}
                  tileSidePx={tileSidePx}
                  label={classNames.resolve(f.aiClassName).display}
                  selected={selectedFindingIds.has(f.id)}
                  active={activeFindingId === f.id}
                  // ANA-R3-TILE-02 — a placeholder has nothing to draw, so it
                  // settles immediately (still counts toward the total).
                  settleId={f.id}
                  onSettled={renderProgress.markSettled}
                  onClick={() => {
                    setActiveImage(f.imageId);
                    setActiveBbox(f.id, "ai");
                  }}
                />
              );
            }
            return (
              <Tile
                key={f.id}
                finding={f}
                image={img}
                displayLabel={classNames.resolve(f.aiClassName).display}
                selected={selectedFindingIds.has(f.id)}
                active={activeFindingId === f.id}
                // ANA-R3-TILE-02 — per-tile settle signal feeding the bottom
                // render-progress bar (fires once when the crop paints or both
                // sources fail).
                onSettled={renderProgress.markSettled}
                // ANA-R3-GEOM-05 — AI-finding tiles get the on-tile polygon
                // editor + session override, gated by the same editAllowed
                // capability that gates Promote. Defect tiles do not (their
                // geometry is edited in the Viewer — GEOM-04).
                editable={editAllowed}
                outlineColor={aiOutlineColor}
                onClick={(event) => {
                  // SB6-ANA-TILE-01 — PLAIN click opens the finding's
                  // source recording image (Viewer + Filmstrip) BEFORE the
                  // selection runs. Modifier clicks (Ctrl/Cmd toggle, Shift
                  // range) are the R58-L-10/L-11 batch-ops path and must NOT
                  // jump the viewer — yanking the image on each Ctrl-click
                  // would fight cross-image multi-select (locked decision,
                  // AC2). Mirrors Filmstrip, which also only activates on
                  // plain click.
                  const isModifier =
                    event.ctrlKey || event.metaKey || event.shiftKey;
                  if (!isModifier) {
                    setActiveImage(f.imageId);
                  }
                  // R58-L-10: standard file-manager modifier behaviour
                  // (plain / Ctrl/Cmd / Shift). The shared facade in
                  // `useTileSelection` keeps the rules in one place so
                  // the Promote button can read the live selection
                  // without duplicating the click semantics.
                  handleTileClick(f.id, event, findingIdsInOrder);
                  // SB6-ANA-TILE-01 — on a PLAIN click, re-stamp the active
                  // selection with the "ai" kind so the List highlights the
                  // matching Findings row + auto-switches to the Findings tab
                  // (R58-L-13 reverse-direction effect). `selectOne` inside
                  // `handleTileClick` clears the kind to null, so this must
                  // run AFTER it. Modifier clicks intentionally leave the kind
                  // untouched — they are cross-image batch selection, not a
                  // single active navigation target.
                  if (!isModifier) {
                    setActiveBbox(f.id, "ai");
                  }
                }}
              />
            );
          })}
          {showDivider ? (
            // SB6-ANA-TILE-01 — in the vertical-grid layout the divider
            // becomes a horizontal hairline spanning the full row,
            // separating the AI-findings block above from the defects
            // block below. `aria-hidden` keeps screen readers traversing
            // straight between options (role="listbox" forbids
            // role="separator" as a child).
            <Box
              data-testid="tiles-section-divider"
              aria-hidden="true"
              sx={{
                gridColumn: "1 / -1",
                height: 2,
                backgroundColor: "divider",
              }}
            />
          ) : null}
          {/* Defect block — second per R58-L-14 AC3. */}
          {defectsInScope.map((d) => {
            const img = imageById.get(d.imageId);
            // ANA-R2-01 AC2 — same count-parity guard as the AI block: a
            // defect whose source image hasn't resolved gets a placeholder
            // tile, never silently dropped.
            if (!img) {
              return (
                <PlaceholderTile
                  key={d.id}
                  tileSidePx={tileSidePx}
                  label={d.catalogueLabel}
                  selected={false}
                  active={false}
                  // ANA-R3-TILE-02 — placeholder settles immediately.
                  settleId={d.id}
                  onSettled={renderProgress.markSettled}
                  onClick={() => {
                    setActiveImage(d.imageId);
                    setActiveBbox(d.id, "defect");
                  }}
                />
              );
            }
            return (
              <DefectTile
                key={d.id}
                tileSidePx={tileSidePx}
                defect={d}
                image={img}
                // ANA-R3-TILE-02 — defect tiles settle too (via the inner Tile
                // crop, or the whole-thumb <img> for the no-bbox path).
                onSettled={renderProgress.markSettled}
                findingForCrop={
                  d.originFindingId
                    ? findingById.get(d.originFindingId)
                    : undefined
                }
                onActivate={() => {
                  // SB6-ANA-TILE-01 AC3 — defect-tile plain click opens
                  // the source image AND selects the defect (List-widget
                  // DefectsPanel pattern). Publishing the "defect" kind
                  // makes the List auto-switch to its Defects tab via the
                  // R58-L-13 reverse-direction effect.
                  setActiveImage(d.imageId);
                  setActiveBbox(d.id, "defect");
                }}
              />
            );
          })}
        </Box>
        {/* ANA-R3-TILE-01-FIX — volume-guard affordance (AC3). When the "all"
            scope exceeds the render cap we render a bounded slice; this line
            tells the user the rendered count vs the true total so the cap is
            never a silent drop (matches the ANA-R2-01 "never silently lose a
            tile" invariant). Full virtualisation is the ANA-R3-TILE-02
            follow-up. */}
        {findingsTruncated ? (
          <Box
            role="status"
            data-testid="tiles-render-cap-notice"
            sx={{ px: 1, py: 1.5, textAlign: "center" }}
          >
            <Typography variant="caption" color="text.secondary">
              Showing first {ALL_SCOPE_RENDER_CAP} of{" "}
              {findingsInScopeAll.length} findings. Narrow the filter or select
              an image to see the rest.
            </Typography>
          </Box>
        ) : null}
      </Box>
      {/* ANA-R3-TILE-02 — render-progress bar, pinned to the BOTTOM of the
          widget chrome (below the flex:1 scroll area, so it never overlaps or
          pushes the grid → no layout shift). It only occupies space while a
          render burst is in flight; on completion it fades out (and the bar
          itself is absolutely positioned so even the in-flight state adds no
          layout to the scroll area). */}
      <TilesRenderProgressBar progress={renderProgress} />
    </Box>
  );
}

interface DefectTileProps {
  defect: Defect;
  image: ImageRef;
  findingForCrop: AiFinding | undefined;
  /**
   * SB6-ANA-TILE-01 — plain-click handler: activate the defect's source
   * image + select the defect. Defect tiles don't participate in the
   * AI-finding multi-select set, so there is no modifier branch here.
   */
  onActivate: () => void;
  /** ANA-R3-TILE-03 — chosen tile-size preset in px (no-bbox <img> path). */
  tileSidePx: number;
  /**
   * ANA-R3-TILE-02 — fired once when this defect tile settles. The bbox path
   * forwards it to the inner `Tile`; the no-bbox whole-thumb path fires it on
   * the `<img>` load OR error (so a broken thumb still settles).
   */
  onSettled?: ((id: string) => void) | undefined;
}

function DefectTile({
  defect,
  image,
  findingForCrop,
  onActivate,
  tileSidePx,
  onSettled,
}: DefectTileProps): React.ReactElement {
  // ANA-R4-TILE-DEFECT-COLOR — defect tiles outline in the DEFECT bbox colour
  // (orange by default), not the AI blue.
  const { prefs } = useLysePreferencesQuery();
  const defectOutlineColor = prefs.boundingBox.defect.color;
  // ANA-R3-TILE-02 — the no-bbox <img> path settles via onLoad/onError; guard
  // so it fires at most once per mount.
  const settledRef = useRef(false);
  const settleImg = useCallback(() => {
    if (settledRef.current) return;
    settledRef.current = true;
    onSettled?.(defect.id);
  }, [onSettled, defect.id]);
  // Crop priority:
  //   1. `defect.bbox` (lyse 1.7.0 — server-derived from defect_path).
  //   2. The originFinding's bbox if the defect was promoted.
  //   3. No bbox → render the whole thumb_900 with a label overlay.
  //
  // (1) and (2) both pass through the same `Tile` canvas-crop helper —
  // Tile takes any object that exposes a `BoundingBox`-shaped `bbox`,
  // so we adapt the defect into a Tile-compatible "pseudo-finding" when
  // option 1 wins.
  const bbox = defect.bbox ?? findingForCrop?.bbox ?? null;
  if (bbox) {
    const cropFinding: AiFinding = findingForCrop ?? {
      id: defect.id,
      // Synthetic pseudo-finding used only for the crop bbox; `classUuid`
      // is unused for rendering — reuse the defect id as a stable filler.
      classUuid: defect.id,
      aiClassName: defect.catalogueLabel,
      confidence: 1,
      status: "pending",
      bbox,
      imageId: defect.imageId,
      processedAt: defect.detectedAt,
    };
    return (
      <Tile
        finding={cropFinding}
        image={image}
        selected={false}
        active={false}
        outlineColor={defectOutlineColor}
        // ANA-R3-TILE-02 — the inner Tile fires onSettled(cropFinding.id);
        // cropFinding.id === defect.id, so it lands in the same settled-id
        // space the widget tracks.
        onSettled={onSettled}
        onClick={() => {
          // SB6-ANA-TILE-01 — no longer a no-op. Plain click opens the
          // source image + selects the defect (List DefectsPanel pattern).
          onActivate();
        }}
        overlay={<DefectLabelOverlay defect={defect} />}
      />
    );
  }
  return (
    <Box
      component="button"
      type="button"
      role="option"
      aria-selected="false"
      aria-label={`${defect.catalogueLabel} — ${defect.status}`}
      onClick={() => {
        // SB6-ANA-TILE-01 — the no-bbox defect tile (whole-thumb preview)
        // is now also clickable: open the source image + select the defect.
        onActivate();
      }}
      sx={{
        position: "relative",
        width: tileSidePx,
        height: tileSidePx,
        p: 0,
        border: "none",
        borderRadius: 1,
        cursor: "pointer",
        flexShrink: 0,
        backgroundColor: "background.paper",
        overflow: "hidden",
      }}
    >
      <Box
        component="img"
        src={image.urls.thumb900}
        alt=""
        loading="lazy"
        // ANA-R3-TILE-02 — settle once the whole-thumb preview resolves (load
        // OR error — a broken thumb still settles so the bar never hangs).
        onLoad={settleImg}
        onError={settleImg}
        sx={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
        }}
      />
      <DefectLabelOverlay defect={defect} />
    </Box>
  );
}

function DefectLabelOverlay({
  defect,
}: {
  defect: Defect;
}): React.ReactElement {
  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      sx={{
        position: "absolute",
        left: 4,
        right: 4,
        bottom: 4,
        px: 0.5,
        py: 0.25,
        borderRadius: 0.75,
        backgroundColor: "rgba(0,0,0,0.6)",
        color: "#FFFFFF",
        backdropFilter: "blur(2px)",
      }}
    >
      {/* UX Q6 (8.0.0) — class-model grade vocabulary on the tile badge;
          ungraded defects show the neutral "Not graded" chip, never a
          fabricated grade. */}
      <DefectGradeChip defect={defect} dense />
      <Typography
        variant="caption"
        sx={{
          flexGrow: 1,
          color: "#FFFFFF",
          fontWeight: 600,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {defect.catalogueLabel}
      </Typography>
      <StatusPill
        status={statusToStatusPill(defect.status)}
        label={defect.status}
        dense
      />
    </Stack>
  );
}

// ANA-R2-01 AC2 — count-parity placeholder. Rendered in place of a real
// `Tile` when the source image metadata hasn't resolved yet (images query in
// flight, or the image sits outside the current page). It keeps the on-screen
// tile count equal to the List/Viewer counts (no click required) and remains
// clickable so the user can still activate the finding/defect; the next render
// swaps it for the cropped tile once `imageById` has the image. Carries the
// same `role="option"` so the listbox semantics + count stay intact.
function PlaceholderTile({
  label,
  selected,
  active,
  onClick,
  tileSidePx,
  settleId,
  onSettled,
}: {
  label: string;
  selected: boolean;
  active: boolean;
  onClick: () => void;
  /** ANA-R3-TILE-03 — chosen tile-size preset in px. */
  tileSidePx: number;
  /** ANA-R3-TILE-02 — finding/defect id this placeholder stands in for. */
  settleId?: string;
  /** ANA-R3-TILE-02 — fired once on mount: a placeholder has nothing to
   *  draw, so it settles immediately (still counts toward the total). */
  onSettled?: (id: string) => void;
}): React.ReactElement {
  // ANA-R3-TILE-02 — settle immediately on mount. Effect (not render) so the
  // parent state update never runs during this component's render.
  useEffect(() => {
    if (settleId !== undefined) onSettled?.(settleId);
  }, [settleId, onSettled]);
  return (
    <Box
      component="button"
      type="button"
      role="option"
      aria-selected={selected ? "true" : "false"}
      aria-current={active ? "true" : "false"}
      aria-label={`Select ${label} (loading image)`}
      data-testid="tile-placeholder"
      onClick={onClick}
      sx={{
        position: "relative",
        width: tileSidePx,
        height: tileSidePx,
        p: 0,
        border: "none",
        borderRadius: 1,
        cursor: "pointer",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "action.hover",
        // ANA-R2-09 — inset box-shadow ring so the selection indicator
        // follows the tile's `border-radius` (a CSS outline draws square
        // corners). Mirrors `Tile`.
        // ANA-R4-TILE-04 — selected = blue ring (film-strip style); was white.
        boxShadow: active
          ? (theme) => `inset 0 0 0 3px ${theme.palette.primary.main}`
          : selected
            ? (theme) => `inset 0 0 0 2px ${theme.palette.primary.light}`
            : "none",
        overflow: "hidden",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          px: 0.5,
          color: "text.secondary",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: "100%",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

// ANA-R3-TILE-02 — render-progress model.
//
// `total`  = the number of tiles the grid mounts for the current scope (AI +
//            defects + placeholders); driven straight off the same in-scope
//            arrays the grid maps over, so it can never disagree with the
//            visible tile count (AC2).
// `drawn`  = how many of those have settled (crop painted, fallback painted, or
//            both sources failed; placeholders settle immediately).
// `visible`= whether the bar should be on screen. Sub-~150 ms renders (fast
//            cache hits) never flip it true (OQ1 — suppress flashes); on
//            completion it holds 100 % for a beat then the bar fades out so it
//            is not permanent chrome (AC5).
interface TilesRenderProgress {
  total: number;
  drawn: number;
  visible: boolean;
  markSettled: (id: string) => void;
}

// Delay before the bar is allowed to appear — renders that settle faster than
// this never show a bar (calm UI; OQ1 resolved → suppress sub-threshold).
const PROGRESS_SHOW_DELAY_MS = 150;
// How long the completed (100 %) bar lingers before it fades out.
const PROGRESS_COMPLETE_HOLD_MS = 450;

function useTilesRenderProgress(
  findingsInScope: AiFinding[],
  defectsInScope: Defect[],
): TilesRenderProgress {
  // A stable signature of the EXACT tile-id set the grid renders this pass.
  // When it changes (image switch, scope/kind toggle, filter change → different
  // ids and/or count) the settled set resets and the bar restarts (AC4). Sorted
  // so a pure reorder isn't mistaken for a new scope.
  const scopeIds = useMemo(() => {
    const ids = [
      ...findingsInScope.map((f) => f.id),
      ...defectsInScope.map((d) => d.id),
    ];
    return ids;
  }, [findingsInScope, defectsInScope]);
  const total = scopeIds.length;
  const scopeKey = useMemo(() => [...scopeIds].sort().join("|"), [scopeIds]);
  const scopeIdSet = useMemo(() => new Set(scopeIds), [scopeIds]);

  const [settled, setSettled] = useState<Set<string>>(() => new Set());
  const [visible, setVisible] = useState(false);

  // The live scope id-set, read inside markSettled without making the callback
  // identity churn every render (markSettled is passed to every tile).
  const scopeIdSetRef = useRef(scopeIdSet);
  scopeIdSetRef.current = scopeIdSet;

  // Reset on scope change (AC4). Drops any settled ids from the previous scope
  // and re-hides the bar; the appear-timer below re-arms for the new set.
  // Skips the FIRST run: on mount the settled set is already empty, and a child
  // tile (e.g. an ANA-R2-01 placeholder that settles in its own mount effect)
  // commits its settle BEFORE this parent effect — resetting here would wipe
  // that immediate settle. We only clear when the key actually changes.
  const prevScopeKeyRef = useRef(scopeKey);
  useEffect(() => {
    if (prevScopeKeyRef.current === scopeKey) return;
    prevScopeKeyRef.current = scopeKey;
    setSettled(new Set());
    setVisible(false);
  }, [scopeKey]);

  const markSettled = useCallback((id: string) => {
    // Ignore late fires from a tile that belonged to a previous scope (its id
    // is no longer in the current set) — they must not inflate `drawn`.
    if (!scopeIdSetRef.current.has(id)) return;
    setSettled((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  // `drawn` only counts ids that are still in scope (defensive against a stale
  // member lingering across a reset within the same tick).
  let drawn = 0;
  for (const id of settled) if (scopeIdSet.has(id)) drawn += 1;

  const complete = total > 0 && drawn >= total;

  // Appear timer (OQ1): when an incomplete render is in flight, arm a 150 ms
  // timer that reveals the bar. If everything settles before it fires, the
  // render was sub-threshold and the bar never shows.
  useEffect(() => {
    if (total === 0 || complete) return;
    const t = window.setTimeout(() => {
      setVisible(true);
    }, PROGRESS_SHOW_DELAY_MS);
    return () => {
      window.clearTimeout(t);
    };
    // Re-arm whenever the scope changes; `complete` flipping true cancels it.
  }, [scopeKey, total, complete]);

  // Completion → hold 100 % briefly, then fade out (AC5). Only matters if the
  // bar actually became visible (a sub-threshold render never set it true).
  useEffect(() => {
    if (!complete || !visible) return;
    const t = window.setTimeout(() => {
      setVisible(false);
    }, PROGRESS_COMPLETE_HOLD_MS);
    return () => {
      window.clearTimeout(t);
    };
  }, [complete, visible]);

  return { total, drawn, visible, markSettled };
}

// ANA-R3-TILE-02 — the slim bottom bar. Absolutely positioned at the widget
// bottom so it never adds layout to (or pushes) the scroll area → no layout
// shift (AC5). Renders nothing for an empty scope or while suppressed.
function TilesRenderProgressBar({
  progress,
}: {
  progress: TilesRenderProgress;
}): React.ReactElement | null {
  const { total, drawn, visible } = progress;
  if (total === 0) return null;
  const pct = total > 0 ? Math.min(100, Math.round((drawn / total) * 100)) : 0;
  return (
    <Fade in={visible} timeout={{ enter: 120, exit: 320 }} unmountOnExit>
      <Box
        // `role="status"` + aria-live so assistive tech hears the progress; the
        // numeric label carries the value (LinearProgress is decorative here).
        role="status"
        aria-live="polite"
        data-testid="tiles-render-progress"
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 0.75,
          borderTop: 1,
          borderColor: "divider",
          backgroundColor: "background.paper",
        }}
      >
        <LinearProgress
          variant="determinate"
          value={pct}
          aria-hidden="true"
          sx={{ flex: 1, height: 4, borderRadius: 2 }}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid="tiles-render-progress-count"
          sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
        >
          {drawn} / {total}
        </Typography>
      </Box>
    </Fade>
  );
}

function CenteredMessage({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <Box
      role="status"
      sx={{
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        p: 2,
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {children}
      </Typography>
    </Box>
  );
}
